import { PROTOCOL_VERSION, type IceServerConfig, type MatchState, type PlayerInput, type PlayerSlot } from "@marshmallow-duel/simulation";

export type MatchSnapshotPayload = Omit<MatchState, "map" | "rules">;

type ControlMessage =
  | { readonly type: "ready"; readonly protocol: number }
  | { readonly type: "input"; readonly sequence: number; readonly tick: number; readonly slot: PlayerSlot; readonly input: PlayerInput }
  | { readonly type: "ping"; readonly sentAt: number }
  | { readonly type: "pong"; readonly sentAt: number }
  | { readonly type: "snapshot"; readonly sequence: number; readonly state: MatchSnapshotPayload }
  | { readonly type: "continue" }
  | { readonly type: "leave" };

type StateMessage = { readonly type: "snapshot"; readonly sequence: number; readonly state: MatchSnapshotPayload };

export interface PeerSessionOptions {
  readonly role: "host" | "guest";
  readonly remotePeerId: string;
  readonly ownSlot: PlayerSlot;
  readonly remoteSlot: PlayerSlot;
  readonly iceServers: readonly IceServerConfig[];
  readonly sendSignal: (targetPeerId: string, signal: { readonly description?: RTCSessionDescriptionInit; readonly candidate?: RTCIceCandidateInit }) => void;
}

const validInput = (value: unknown): value is PlayerInput => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const input = value as Record<string, unknown>;
  return ["left", "right", "up", "down", "shield", "rope", "ability"].every((key) => typeof input[key] === "boolean");
};

const validSnapshot = (value: unknown): value is MatchSnapshotPayload => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const state = value as Record<string, unknown>;
  return Number.isSafeInteger(state.tick) && typeof state.phase === "string" && Array.isArray(state.players) && Array.isArray(state.projectiles) && Array.isArray(state.pickups) && Array.isArray(state.ropes);
};

export const snapshotPayload = (state: MatchState): MatchSnapshotPayload => {
  const { map: _map, rules: _rules, ...snapshot } = state;
  return snapshot;
};

export class PeerSession {
  readonly role: "host" | "guest";
  readonly remotePeerId: string;
  readonly remoteSlot: PlayerSlot;
  private readonly ownSlot: PlayerSlot;
  private readonly connection: RTCPeerConnection;
  private readonly sendSignal: PeerSessionOptions["sendSignal"];
  private controlChannel: RTCDataChannel | null = null;
  private stateChannel: RTCDataChannel | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private pingTimer = 0;
  private inputSequence = 0;
  private snapshotSequence = 0;
  private lastInputSequence = -1;
  private lastSnapshotSequence = -1;
  private didConnect = false;
  private intentionallyClosed = false;
  remoteInput: PlayerInput | null = null;
  latency = 0;
  onConnected: () => void = () => undefined;
  onDisconnected: () => void = () => undefined;
  onSnapshot: (snapshot: MatchSnapshotPayload) => void = () => undefined;
  onContinueRequested: () => void = () => undefined;
  onProtocolMismatch: () => void = () => undefined;

  constructor(options: PeerSessionOptions) {
    this.role = options.role;
    this.remotePeerId = options.remotePeerId;
    this.remoteSlot = options.remoteSlot;
    this.ownSlot = options.ownSlot;
    this.sendSignal = options.sendSignal;
    this.connection = new RTCPeerConnection({
      iceServers: options.iceServers.map((server) => ({
        urls: typeof server.urls === "string" ? server.urls : Array.from(server.urls),
        username: server.username,
        credential: server.credential
      })),
      bundlePolicy: "max-bundle",
      iceCandidatePoolSize: 2
    });
    this.connection.addEventListener("icecandidate", this.onIceCandidate);
    this.connection.addEventListener("connectionstatechange", this.onConnectionStateChange);
    if (this.role === "host") {
      this.attachControlChannel(this.connection.createDataChannel("control", { ordered: true }));
      this.attachStateChannel(this.connection.createDataChannel("state", { ordered: false, maxRetransmits: 0 }));
    } else {
      this.connection.addEventListener("datachannel", this.onDataChannel);
    }
  }

  async start(): Promise<void> {
    if (this.role !== "host") return;
    const offer = await this.connection.createOffer();
    await this.connection.setLocalDescription(offer);
    if (this.connection.localDescription) this.sendSignal(this.remotePeerId, { description: this.connection.localDescription.toJSON() });
  }

  async handleSignal(message: { readonly description?: RTCSessionDescriptionInit; readonly candidate?: RTCIceCandidateInit }): Promise<void> {
    if (message.candidate) {
      if (!this.connection.remoteDescription) this.pendingCandidates.push(message.candidate);
      else await this.connection.addIceCandidate(message.candidate);
    }
    if (!message.description) return;
    if (message.description.type === "offer" && this.role === "guest") {
      await this.connection.setRemoteDescription(message.description);
      await this.flushCandidates();
      const answer = await this.connection.createAnswer();
      await this.connection.setLocalDescription(answer);
      if (this.connection.localDescription) this.sendSignal(this.remotePeerId, { description: this.connection.localDescription.toJSON() });
    } else if (message.description.type === "answer" && this.role === "host") {
      await this.connection.setRemoteDescription(message.description);
      await this.flushCandidates();
    }
  }

  sendInput(tick: number, input: PlayerInput): void {
    if (this.role !== "guest") return;
    this.sendControl({ type: "input", sequence: this.inputSequence++, tick, slot: this.ownSlot, input });
  }

  sendSnapshot(state: MatchState, reliable = false): void {
    if (this.role !== "host") return;
    const message = { type: "snapshot", sequence: this.snapshotSequence++, state: snapshotPayload(state) } as const;
    if (reliable) this.sendControl(message);
    else if (this.stateChannel?.readyState === "open" && this.stateChannel.bufferedAmount < 512_000) this.stateChannel.send(JSON.stringify(message satisfies StateMessage));
  }

  requestContinue(): void {
    if (this.role === "guest") this.sendControl({ type: "continue" });
  }

  close(notify = true): void {
    if (this.intentionallyClosed) return;
    if (notify) this.sendControl({ type: "leave" });
    this.intentionallyClosed = true;
    clearInterval(this.pingTimer);
    this.controlChannel?.close();
    this.stateChannel?.close();
    this.connection.close();
  }

  private attachControlChannel(channel: RTCDataChannel): void {
    this.controlChannel = channel;
    channel.addEventListener("open", this.checkConnected);
    channel.addEventListener("close", this.onChannelClosed);
    channel.addEventListener("error", this.onChannelClosed);
    channel.addEventListener("message", this.onControlMessage);
  }

  private attachStateChannel(channel: RTCDataChannel): void {
    this.stateChannel = channel;
    channel.addEventListener("open", this.checkConnected);
    channel.addEventListener("close", this.onChannelClosed);
    channel.addEventListener("error", this.onChannelClosed);
    channel.addEventListener("message", this.onStateMessage);
  }

  private readonly onDataChannel = (event: RTCDataChannelEvent): void => {
    if (event.channel.label === "control") this.attachControlChannel(event.channel);
    else if (event.channel.label === "state") this.attachStateChannel(event.channel);
  };

  private readonly checkConnected = (): void => {
    if (this.didConnect || this.controlChannel?.readyState !== "open" || this.stateChannel?.readyState !== "open") return;
    this.didConnect = true;
    this.sendControl({ type: "ready", protocol: PROTOCOL_VERSION });
    this.pingTimer = window.setInterval(() => this.sendControl({ type: "ping", sentAt: Date.now() }), 3_000);
    this.onConnected();
  };

  private readonly onControlMessage = (event: MessageEvent<string>): void => {
    if (typeof event.data !== "string" || event.data.length > 524_288) return;
    try {
      const message = JSON.parse(event.data) as ControlMessage;
      if (message.type === "ready" && message.protocol !== PROTOCOL_VERSION) this.onProtocolMismatch();
      else if (message.type === "input" && this.role === "host" && message.slot === this.remoteSlot && Number.isSafeInteger(message.sequence) && message.sequence > this.lastInputSequence && validInput(message.input)) {
        this.lastInputSequence = message.sequence;
        this.remoteInput = message.input;
      } else if (message.type === "ping" && Number.isFinite(message.sentAt)) this.sendControl({ type: "pong", sentAt: message.sentAt });
      else if (message.type === "pong" && Number.isFinite(message.sentAt)) this.latency = Math.max(0, Math.min(60_000, Math.round((Date.now() - message.sentAt) / 2)));
      else if (message.type === "snapshot" && this.role === "guest") this.acceptSnapshot(message.sequence, message.state);
      else if (message.type === "continue" && this.role === "host") this.onContinueRequested();
      else if (message.type === "leave") this.disconnect();
    } catch {
      // Invalid peer packets never reach the simulation.
    }
  };

  private readonly onStateMessage = (event: MessageEvent<string>): void => {
    if (this.role !== "guest" || typeof event.data !== "string" || event.data.length > 524_288) return;
    try {
      const message = JSON.parse(event.data) as StateMessage;
      if (message.type === "snapshot") this.acceptSnapshot(message.sequence, message.state);
    } catch {
      // Unreliable stale or malformed snapshots are skipped.
    }
  };

  private acceptSnapshot(sequence: number, state: unknown): void {
    if (!Number.isSafeInteger(sequence) || sequence <= this.lastSnapshotSequence || !validSnapshot(state)) return;
    this.lastSnapshotSequence = sequence;
    this.onSnapshot(state);
  }

  private sendControl(message: ControlMessage): void {
    if (this.controlChannel?.readyState === "open") this.controlChannel.send(JSON.stringify(message));
  }

  private readonly onIceCandidate = (event: RTCPeerConnectionIceEvent): void => {
    if (event.candidate) this.sendSignal(this.remotePeerId, { candidate: event.candidate.toJSON() });
  };

  private readonly onConnectionStateChange = (): void => {
    if (this.connection.connectionState === "failed" || this.connection.connectionState === "closed") this.disconnect();
  };

  private readonly onChannelClosed = (): void => this.disconnect();

  private disconnect(): void {
    if (this.intentionallyClosed) return;
    this.intentionallyClosed = true;
    clearInterval(this.pingTimer);
    this.controlChannel?.close();
    this.stateChannel?.close();
    this.connection.close();
    this.onDisconnected();
  }

  private async flushCandidates(): Promise<void> {
    for (const candidate of this.pendingCandidates.splice(0)) await this.connection.addIceCandidate(candidate);
  }
}
